import { useMemo, useState } from "react";
import {
  calculateLineItemTotal,
  calculateTaxAmount,
  calculateEntityTotals,
} from "../../../_utils/pricing";

/**
 * Owns an entity's pricing collections (plans/products/one-time line items/
 * discount/taxes) as plain state - NOT react-hook-form fields, since these
 * are dynamic collections with computed totals, not form scalars (mirrors
 * how the old BillingItems engine modeled them too). Entity-agnostic - used
 * by both the proposal wizard and the subscription modal, driving the same
 * `_utils/pricing.js` math; nothing here references "proposal" or
 * "subscription" specifically.
 *
 * Line items and taxes each keep a RAW state array (editable fields only)
 * and derive a DISPLAY array via useMemo (adding the computed
 * totalPrice/totalTax) - never the reverse. The old TaxTable recomputed
 * tax amounts inside a useEffect that called setState on every amount/
 * taxData change (a recompute-in-effect loop); deriving via useMemo here
 * means there's no effect at all, just a pure calculation that reruns when
 * its inputs change.
 *
 * Tax amounts depend on the PRE-tax total, which itself depends on plans/
 * products/lineItems/discount - so totals are computed in two passes:
 * once without taxes to get totalAmountBeforeTax, then each tax row's
 * amount is derived against that, then the real totals (this time with
 * taxes) are computed for real. Both passes are memoized pure functions,
 * not effects.
 */
export function usePricingState(initial = {}) {
  const [plans, setPlans] = useState(initial.plans || []);
  const [products, setProducts] = useState(initial.products || []);
  const [rawLineItems, setLineItems] = useState(initial.lineItems || []);
  const [discount, setDiscount] = useState(initial.discount || { type: "Flat", value: "" });
  const [rawTaxes, setTaxes] = useState(initial.taxes || []);

  const lineItems = useMemo(
    () => rawLineItems.map((item) => ({ ...item, totalPrice: calculateLineItemTotal(item) })),
    [rawLineItems]
  );

  const preTaxTotals = useMemo(
    () => calculateEntityTotals({ plans, products, lineItems, discount, taxes: [] }),
    [plans, products, lineItems, discount]
  );

  const taxes = useMemo(
    () =>
      rawTaxes.map((tax) => ({
        ...tax,
        totalTax: calculateTaxAmount(tax.taxType, tax.taxValue, preTaxTotals.totalAmountBeforeTax),
      })),
    [rawTaxes, preTaxTotals.totalAmountBeforeTax]
  );

  const totals = useMemo(
    () => calculateEntityTotals({ plans, products, lineItems, discount, taxes }),
    [plans, products, lineItems, discount, taxes]
  );

  return {
    plans,
    setPlans,
    products,
    setProducts,
    lineItems,
    setLineItems,
    discount,
    setDiscount,
    taxes,
    setTaxes,
    totals,
  };
}
